// useBenefitToggle.ts (Custom Hook)
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {createEisTypeObject, isDefined} from 'Quoting/utils/utilities';
import {Map as ImmutableMap, fromJS} from 'immutable';
import {updateBackdropState, updateOffer} from 'Quoting/ducks/createQuote';

export const useBenefitToggle = () => {
  const dispatch = useDispatch();
  const {getQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();
  const {selectedPlan, defaultPlanValues} = useSelector((state: ReducerState) => state.quoteForm);

  const planRootId = getQuoteFieldValue(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}]._key.rootId`);
  const coverageDefId = getQuoteFieldValue(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}].coverageDefs[0]._key.rootId`);

  const onToggleBenefitApply = async(
    benefitEntityName: string,
    benefitAppliedPath: string,
    value: boolean,
    benefitDetailsPath: string
  ) => {
    const fieldsToUpdate: {path: string, newValue: any}[] = [
      {
        path: benefitAppliedPath,
        newValue: value
      }
    ];

    if (value && !isDefined(getQuoteFieldValue(benefitDetailsPath))) {
      const benefitDetails = getBenefitDetailsObject(
        benefitEntityName,
        defaultPlanValues?.get('GVCI4'),
        planRootId ?? '',
        coverageDefId ?? ''
      );

      fieldsToUpdate.push({
        path: benefitDetailsPath,
        newValue: fromJS(benefitDetails)
      });
    }

    updateQuoteFieldValues(fieldsToUpdate);
    dispatch(updateBackdropState(true));
    await new Promise<void>((resolve) => {
      dispatch(updateOffer(resolve))
    });
    dispatch(updateBackdropState(false));
  };

  const getBenefitDetailsObject = (
    benefitEntityName: string,
    defaultValues: ImmutableMap<string, any> | undefined,
    rootId: string,
    parentId: string
  ) => {
    switch (benefitEntityName) {
      case 'skinCancerRider':
        return {
          benefitAmount: {amount: +defaultValues.get('CISkinCancerRider.benefitAmount'), currency: 'USD'},
          benefitTypeCd: defaultValues.get('CISkinCancerRider.benefitTypeCd'),
          timePeriod: defaultValues.get('CISkinCancerRider.timePeriod') ?? 'CalendarYear',
          isSquamousCellCarcinomaOfTheSkin: defaultValues.get('CISkinCancerRider.isSquamousCellCarcinomaOfTheSkin'),
          isBasalCellCarcinoma: defaultValues.get('CISkinCancerRider.isBasalCellCarcinoma'),
          exclusions: [
            {
              isExclusion: defaultValues.get('CISkinCancerRiderExclIntentionallySelfInflictedInjury.isExclusion') ?? true,
              ...createEisTypeObject('CISkinCancerRiderExclIntentionallySelfInflictedInjury', rootId, parentId),
            },
            {
              isExclusion: defaultValues.get('CISkinCancerRiderExclAlcoholAbuse.isExclusion') ?? true,
              ...createEisTypeObject('CISkinCancerRiderExclAlcoholAbuse', rootId, parentId),
            },
            {
              isExclusion: defaultValues.get('CISkinCancerRiderExclDrugAbuse.isExclusion') ?? true,
              ...createEisTypeObject('CISkinCancerRiderExclDrugAbuse', rootId, parentId),
            },
            {
              isExclusion: defaultValues.get('CISkinCancerRiderExclDiagnosedBeforeEffectiveDate.isExclusion') ?? true,
              ...createEisTypeObject('CISkinCancerRiderExclDiagnosedBeforeEffectiveDate', rootId, parentId),
            },
            {
              isExclusion: defaultValues.get('CISkinCancerRiderExclSuicide.isExclusion') ?? true,
              ...createEisTypeObject('CISkinCancerRiderExclSuicide', rootId, parentId),
            },
            {
              isExclusion: defaultValues.get('CISkinCancerRiderExclAppropriateHealthService.isExclusion') ?? false,
              ...createEisTypeObject('CISkinCancerRiderExclAppropriateHealthService', rootId, parentId),
            },
            {
              isExclusion:
                defaultValues.get('CISkinCancerRiderExclMalignantMelanoma.isExclusion') ?? true,
              ...createEisTypeObject('CISkinCancerRiderExclMalignantMelanoma', rootId, parentId),
            },
            {
              isExclusion: defaultValues.get('CISkinCancerRiderExclAnyConditionsConsideredPreCancerous.isExclusion') ?? false,
              ...createEisTypeObject('CISkinCancerRiderExclAnyConditionsConsideredPreCancerous', rootId, parentId),
            },
            {
                isExclusion: defaultValues.get('CISkinCancerRiderExclIllegalActivitiesOrOccupation.isExclusion') ?? true,
                ...createEisTypeObject('CISkinCancerRiderExclIllegalActivitiesOrOccupation', rootId, parentId),
              },
          ],
          ...createEisTypeObject('CISkinCancerRider', rootId, parentId),
        };
      case 'specifiedChronicIllnessRider':
        return {
          planBenefitAmountPct: defaultValues.get('CISpecifiedChronicIllnessRider.planBenefitAmountPct'),
          isAdrenalInsufficiency: defaultValues.get('CISpecifiedChronicIllnessRider.isAdrenalInsufficiency'),
          isAmyotrophicLateralSclerosis: defaultValues.get('CISpecifiedChronicIllnessRider.isAmyotrophicLateralSclerosis'),
          isArthritis: defaultValues.get('CISpecifiedChronicIllnessRider.isArthritis'),
          isHuntingtonsChorea: defaultValues.get('CISpecifiedChronicIllnessRider.isHuntingtonsChorea'),
          isMultipleSclerosis: defaultValues.get('CISpecifiedChronicIllnessRider.isMultipleSclerosis'),
          isSCIMuscularDystrophy: defaultValues.get('CISpecifiedChronicIllnessRider.isSCIMuscularDystrophy'),
          isOsteomyelitis: defaultValues.get('CISpecifiedChronicIllnessRider.isOsteomyelitis'),
          isOsteoporosis: defaultValues.get('CISpecifiedChronicIllnessRider.isOsteoporosis'),
          exclusions: [
            {
              ...createEisTypeObject('CIExclSCIBenefitIntentionallySelfInflictedInjury', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessRider.CIExclSCIBenefitIntentionallySelfInflictedInjury'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIBenefitAlcoholAbuse', rootId, parentId),
              isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitAlcoholAbuse'),
            },
            {
              ...createEisTypeObject('CIExclSCIBenefitDrugAbuse', rootId, parentId),
              isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitDrugAbuse'),
            },
            {
              ...createEisTypeObject('CIExclSCIBenefitDiagnosedBeforeEffectiveDate', rootId, parentId),
              isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitDiagnosedBeforeEffectiveDate'),
            },
            {
              ...createEisTypeObject('CIExclSCIBenefitIllegalActivitiesOrOccupation', rootId, parentId),
              isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitIllegalActivitiesOrOccupation'),
            },
            {
              ...createEisTypeObject('CIExclSCIBenefitSuicide', rootId, parentId),
              isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitSuicide'),
            },
            {
                ...createEisTypeObject('CIExclSCIBenefitAddisonDisease', rootId, parentId),
                isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitAddisonDisease'),
            },
            {
                ...createEisTypeObject('CIExclSCIBenefitCertainMultipleSclerosisDiagnoses', rootId, parentId),
                isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitCertainMultipleSclerosisDiagnoses'),
            },
            {
                ...createEisTypeObject('CIExclSCIBenefitAppropriateHealthcareServicesMDOnly', rootId, parentId),
                isExclusion: defaultValues.get('CISpecifiedChronicIllnessRider.CIExclSCIBenefitAppropriateHealthcareServicesMDOnly'),
            },
          ],
          ...createEisTypeObject('CISpecifiedChronicIllnessRider', rootId, parentId),
        };
      case 'specifiedChronicIllnessAndAccidentalInjuryRider':
        return {
          planBenefitAmountPct: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.planBenefitAmountPct'),
          injuryPct: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.injuryPct'),
          isAdrenalInsufficiency: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.isAdrenalInsufficiency'),
          isAmyotrophicLateralSclerosis: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.isAmyotrophicLateralSclerosis'),
          isArthritis: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.isArthritis'),
          isHuntingtonsChorea: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.isHuntingtonsChorea'),
          isMultipleSclerosis: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.isMultipleSclerosis'),
          isSCIMuscularDystrophy: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.isSCIMuscularDystrophy'),
          isOsteomyelitis: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.isOsteomyelitis'),
          isOsteoporosis: defaultValues.get('CISpecifiedChronicIllnessAndAccidentalInjuryRider.isOsteoporosis'),
          exclusions: [
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitIntentionallySelfInflictedInjury', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitIntentionallySelfInflictedInjury'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitAlcoholAbuse', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitAlcoholAbuse'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitDrugAbuse', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitDrugAbuse'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitDiagnosedBeforeEffectiveDate', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitDiagnosedBeforeEffectiveDate'
              ) ?? true,
            },
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitIllegalActivitiesOrOccupation', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitIllegalActivitiesOrOccupation'
              ),
            },
            {
              ...createEisTypeObject('CIExclSCIandAIBenefitSuicide', rootId, parentId),
              isExclusion: defaultValues.get(
                'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitSuicide'
              ),
            },
            {
                ...createEisTypeObject('CIExclSCIandAIBenefitAddisonsDiseaseDoesNotIncludeSecondaryAndTertiaryAdrenalInsufficiency', rootId, parentId),
                isExclusion: defaultValues.get(
                  'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitAddisonsDiseaseDoesNotIncludeSecondaryAndTertiaryAdrenalInsufficiency'
                ),
            },
            {
                ...createEisTypeObject('CIExclSCIandAIBenefitCertainMultipleSclerosisDiagnoses', rootId, parentId),
                isExclusion: defaultValues.get(
                  'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitCertainMultipleSclerosisDiagnoses'
                ),
            },
            {
                ...createEisTypeObject('CIExclSCIandAIBenefitAppropriateHealthCareServices', rootId, parentId),
                isExclusion: defaultValues.get(
                  'CISpecifiedChronicIllnessAndAccidentalInjuryRider.CIExclSCIandAIBenefitAppropriateHealthCareServices'
                ),
            },
          ],
          ...createEisTypeObject('CISpecifiedChronicIllnessAndAccidentalInjuryRider', rootId, parentId),
        };
      default:
        return {};
    }
  };

  return {
    onToggleBenefitApply,
    getBenefitDetailsObject,
  };
}

export const isWellnessServicesDisabled = (situsStateCd: string) => ['CO','MN','MI','MT'].includes(situsStateCd);